import React, { FC } from 'react';
import Link from 'next/link';
import {
  dateParser,
  expenseParser,
  isTrackingInformationAvailableInRegion,
  orderDetailsMarkup,
  OrderDetailsProps,
  Styled,
} from './';
import { LinkText } from 'components/Table/Table.styles';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { fuzzyFilter } from 'components/Table';
import { getCreditCardLogo } from 'components/OrderPayment/OrderPayment.helper';
import Image from 'next/legacy/image';
import { PageTitle } from 'styles/sharedStyles';
import { useI18n } from 'contexts/i18n';
import TrackingInformation from 'components/OrderDetails/TrackingInformation';
import { formatDateToMonthYear } from 'utils';
import { ExpirationDate } from 'components/ShoppingCart/ShoppingCart.styles';
import { useAddresses } from 'reactQuery/queries';
import {
  getNameFromAddress,
  getMailingNameFromAddress,
  isBillingAddressAvailable,
} from 'components/OrderAddress';

const OrderDetails: FC<OrderDetailsProps> = ({ order }) => {
  const i18n = useI18n();
  const data = order.lineItems;
  const isTrackingInformationAvailable =
    isTrackingInformationAvailableInRegion() && order.orderTracking.length > 0;
  const { data: doctorAddress } = useAddresses();

  const showBillingAddress = isBillingAddressAvailable(
    doctorAddress?.billingAddresses
  );

  const columnHelper = createColumnHelper<any>();

  const columns: any = [
    columnHelper.accessor('imageUrl', {
      header: () => i18n['Item(s) Ordered'],
      cell: info => <ProductImage imageUrl={info.getValue()} />,
    }),
    columnHelper.accessor('name', {
      header: () => null,
      cell: info => (
        <OrderItem
          itemName={info.getValue()}
          discontinuedOn={info.row.original.discontinueOn}
          isSubscribed={info.row.original.isSubscribed}
          quantity={info.row.original.quantity}
          subscriptionFrequency={info.row.original.subscriptionFrequencyTitle}
          discountPercentage={info.row.original.discountPercentage}
          isParentSubscription={order.parentSubscriptionOrder}
        />
      ),
    }),
    columnHelper.accessor('quantity', {
      header: () => orderDetailsMarkup(i18n).quantity,
    }),
  ];

  if (order.subscriptionNumbers?.length > 0) {
    columns.push(
      columnHelper.accessor('subscriptionNumber', {
        header: () => orderDetailsMarkup(i18n).subscriptionNumber,
        cell: ({ getValue }) => {
          const subscriptionNumber = getValue();
          return (
            <Link
              href="/subscribe-and-save?selected=mysubscriptions"
              passHref
              legacyBehavior
            >
              <LinkText>{subscriptionNumber}</LinkText>
            </Link>
          );
        },
      })
    );
  }

  if (order.redemptionPoints) {
    columns.push(
      columnHelper.accessor('points', {
        header: () => orderDetailsMarkup(i18n).points,
      })
    );
  }

  columns.push(
    columnHelper.accessor('price', {
      header: () => orderDetailsMarkup(i18n).price,
    })
  );

  columns.push(
    columnHelper.accessor('total', {
      header: () => orderDetailsMarkup(i18n).total,
    })
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    filterFns: {
      fuzzy: fuzzyFilter,
    },
  });

  return (
    <>
      <Styled.Container>
        <Styled.ColumnParent>
          <Styled.OrderHeadline>
            <PageTitle>{i18n['Order Details']}</PageTitle>
            <Styled.OrderNumberParent>
              {i18n['Order Number']}:{' '}
              <Styled.OrderNumber style={{ fontWeight: '500' }}>
                {order.orderNumber}
              </Styled.OrderNumber>
            </Styled.OrderNumberParent>
          </Styled.OrderHeadline>
        </Styled.ColumnParent>
      </Styled.Container>
      <Styled.Container>
        <Styled.ColumnParent>
          <Styled.ColumnBody>
            <Styled.TableComponent>
              <thead>
                {table.getHeaderGroups().map(headerGroup => (
                  <Styled.TableHeadRow key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <Styled.TableHeadCell key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </Styled.TableHeadCell>
                    ))}
                  </Styled.TableHeadRow>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map(row => (
                  <Styled.TableCellRow key={row.id}>
                    {row.getVisibleCells().map(cell => (
                      <Styled.TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </Styled.TableCell>
                    ))}
                  </Styled.TableCellRow>
                ))}
              </tbody>
            </Styled.TableComponent>
          </Styled.ColumnBody>

          {isTrackingInformationAvailable ? (
            <TrackingInformation order={order} />
          ) : null}

          <Styled.ColumnBody>
            <Styled.HeadParent>
              <Styled.HeadBody>
                {i18n['Shipping / Billing details']}
              </Styled.HeadBody>
            </Styled.HeadParent>
            <Styled.DescriptionRow showBillingAddress={showBillingAddress}>
              <Styled.DescriptionCell>
                <Styled.HighlightedText>
                  {i18n['Shipping Address']}
                </Styled.HighlightedText>
                <Styled.RegularText>
                  {getNameFromAddress(order.shippingAddress).length > 0 ? (
                    <>
                      {getNameFromAddress(order.shippingAddress)} <br />
                    </>
                  ) : (
                    ''
                  )}
                  {getMailingNameFromAddress(order.shippingAddress)}
                  <br />
                  {i18n['Phone']} {order.shippingAddress?.phone}
                  <br />
                  {order.shippingAddress?.address1}{' '}
                  {order.shippingAddress?.address2}{' '}
                  {order.shippingAddress?.address3}
                  <br />
                  {order.shippingAddress?.city},{' '}
                  {order.shippingAddress?.stateName}{' '}
                  {order.shippingAddress?.zipcode}
                  <br />
                  {order.shippingAddress.countryName}
                </Styled.RegularText>
              </Styled.DescriptionCell>
              {showBillingAddress ? (
                <Styled.DescriptionCell>
                  <Styled.HighlightedText>
                    {i18n['Billing Address']}
                  </Styled.HighlightedText>
                  <Styled.RegularText>
                    {getNameFromAddress(order.billingAddress).length > 0 ? (
                      <>
                        {getNameFromAddress(order.billingAddress)} <br />
                      </>
                    ) : (
                      ''
                    )}
                    {getMailingNameFromAddress(order.billingAddress)}
                    <br />
                    {i18n['Phone']} {order.billingAddress?.phone}
                    <br />
                    {order.billingAddress.address1}{' '}
                    {order.billingAddress?.address2}{' '}
                    {order.billingAddress?.address3}
                    <br />
                    {order.billingAddress?.city},{' '}
                    {order.billingAddress?.stateName}{' '}
                    {order.billingAddress.zipcode} <br />
                    {order.billingAddress.countryName}
                  </Styled.RegularText>
                </Styled.DescriptionCell>
              ) : null}
              {order.creditCardLast4Digits && (
                <Styled.DescriptionCell>
                  <Styled.HighlightedText>
                    {i18n['Payment Method']}
                  </Styled.HighlightedText>
                  <Styled.RegularText>
                    <Styled.CardImageParent>
                      <Image
                        src={getCreditCardLogo(order.creditCardType)}
                        width="40"
                        height="40"
                        alt="credit card logo"
                      />
                    </Styled.CardImageParent>
                    {i18n['ending in']} {order.creditCardLast4Digits}
                  </Styled.RegularText>
                </Styled.DescriptionCell>
              )}
            </Styled.DescriptionRow>
          </Styled.ColumnBody>
        </Styled.ColumnParent>
        <Styled.ColumnParent orderSummary>
          <Styled.ColumnBody>
            <Styled.HeadParent>
              <Styled.HeadBody>{i18n['Order Summary']}</Styled.HeadBody>
              {/* TODO: add order status here  */}
              {/* <Styled.StatusText>{order.status}</Styled.StatusText> */}
            </Styled.HeadParent>
            <div>
              <Styled.InfoParent>
                {dateParser(order, i18n).map(date => (
                  <Styled.InfoRow key={date.name}>
                    <Styled.InfoType>{date.name}</Styled.InfoType>
                    <Styled.InfoValue>{date.value()}</Styled.InfoValue>
                  </Styled.InfoRow>
                ))}
              </Styled.InfoParent>
              <Styled.InfoParent>
                {expenseParser(order, i18n).map(
                  expense =>
                    expense.value && (
                      <Styled.InfoRow key={expense.name}>
                        <Styled.InfoType>{expense.name}</Styled.InfoType>
                        <Styled.InfoValue
                          isDiscountType={expense.isDiscountType}
                        >
                          {expense.value}
                        </Styled.InfoValue>
                      </Styled.InfoRow>
                    )
                )}
              </Styled.InfoParent>
              <Styled.InfoParent>
                <Styled.TotalRow>
                  <div>{i18n['Total']}</div>
                  <div>{order.total}</div>
                </Styled.TotalRow>
              </Styled.InfoParent>
            </div>
          </Styled.ColumnBody>
        </Styled.ColumnParent>
      </Styled.Container>
    </>
  );
};

const ProductImage = ({ imageUrl }: { imageUrl: string }) => (
  <Styled.ProductImage src={imageUrl} alt="Product Image" />
);
const OrderItem = ({
  itemName,
  discontinuedOn,
  isSubscribed,
  quantity,
  subscriptionFrequency,
  discountPercentage,
  isParentSubscription,
}: {
  itemName: string;
  discontinuedOn?: string;
  isSubscribed: boolean;
  quantity: number;
  subscriptionFrequency: string;
  discountPercentage: string;
  isParentSubscription: boolean;
}) => {
  const i18n = useI18n();
  const expireDate = formatDateToMonthYear(discontinuedOn);
  return (
    <Styled.OrderItem>
      {itemName}
      {expireDate ? (
        <ExpirationDate>
          {i18n['Expires on']} {expireDate}
        </ExpirationDate>
      ) : null}
      <Styled.SubscriptionDisclaimer>
        {isSubscribed &&
          (isParentSubscription
            ? `${i18n['Subscription:']} ${quantity} ${
                quantity === 1 ? i18n['Unit'] : i18n['Units']
              } ${i18n['delivered and billed every']} ${
                i18n[subscriptionFrequency] || subscriptionFrequency
              }`
            : 'Subscription Delivery')}
        {parseInt(discountPercentage) > 0 && (
          <Styled.PercentageSaved>
            {discountPercentage}% {i18n['saved']}
          </Styled.PercentageSaved>
        )}
      </Styled.SubscriptionDisclaimer>
    </Styled.OrderItem>
  );
};

export default OrderDetails;
